// NC1 signup bootstrap — "identity born at website signup" (AWS-PORT.md
// chunk 1; v2 per EPOCH-KEYPAIR.md).
//
// Called from Login.onAuthentication (the universal post-auth funnel, so
// email/password AND Google/hosted-UI signups take the same path — no OAuth
// special-case). For a brand-new account it:
//   1. probes GET /e2ee/account (also yields the altAccountId),
//   2. generates the identity (X25519 + Ed25519) and the epoch-0 KEYPAIR
//      (v2: uploaders seal per-record keys to its public half; only members
//      ever hold the secret half),
//   3. wraps the identity secrets under a fresh 160-bit recovery code
//      (always) and, where the platform supports WebAuthn PRF, under a new
//      passkey too,
//   4. publishes the ONE-transaction bootstrap (bundle + genesis device list
//      + owner-only member list carrying the epoch public key + the epoch
//      secret sealed to the owner + genesis log triplet),
//   5. caches the unwrapped identity in the IndexedDB keystore,
//   6. returns the recovery code for the require-acknowledge dialog.
//
// NEVER blocks signup: any failure sets a retry marker and the next login
// retries. Existing accounts without the marker are untouched (Virgil
// accounts keep working exactly as before — the coexistence prime directive).

import E2eeApi from '@/aws/e2ee/E2eeApi'
import { getSodium } from './sodium'
import * as Keystore from './Keystore'
import { forgetPinnedHead } from './Decryptor'
import { prfProbablySupported, generatePrfSaltB64 } from './PasskeyPrf'
import { passkeyMode } from './PasskeyPolicy'
import { wrapSecrets, wrapToWebauthn } from './shared/kek.js'
import { generateRecoveryCode, recoveryCodeDisplay } from './shared/recovery.js'
import {
  b64, ub64, epochKeypair, deviceListSignedBytes, memberListSignedBytes,
} from './shared/envelope.js'
import { canonicalizeBytes } from './shared/jcs.js'
import {
  KIND_IDENTITY, KIND_DEVICE_LIST, KIND_MEMBER_LIST,
  docHashIdentity, docHashDeviceList, docHashMemberList,
  signEntry, entryHash,
} from './shared/translog.js'

const pendingKey = (email) => `nc1.bootstrap.pending.${email}`

// identityVersion is 1 at signup; a viewer-only identity RESET (finding ⑯)
// re-genesises at old+1 with the same document shapes.
function buildBootstrap(sodium, { email, altAccountId, wraps, idSign, idEnc, epoch, identityVersion = 1 }) {
  const ts = Date.now()

  const identityBundle = {
    email,
    userId: altAccountId,
    identityVersion,
    idEncPk: b64(sodium, idEnc.publicKey),
    idSignPk: b64(sodium, idSign.publicKey),
    wraps,
  }

  // Genesis device list is EMPTY: the web is an identity holder, never an
  // upload device. Windows machines join later by having their signing key
  // admitted to this list.
  const deviceList = { userId: altAccountId, version: 1, devices: [] }
  deviceList.identitySig = b64(sodium,
    sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), idSign.privateKey))

  const memberList = {
    accountId: altAccountId,
    version: 1,
    keyEpoch: 0,
    // v2: the epoch-0 PUBLIC key — the only key an uploader needs.
    epochPk: b64(sodium, epoch.epochPk),
    members: [{
      userId: altAccountId,
      email,
      idEncPk: identityBundle.idEncPk,
      idSignPk: identityBundle.idSignPk,
      role: 'owner',
      joinedAt: ts,
    }],
  }
  memberList.ownerSig = b64(sodium,
    sodium.crypto_sign_detached(memberListSignedBytes(memberList), idSign.privateKey))

  // The epoch-0 SECRET sealed to the owner's identity key (row shape
  // unchanged from v1 — only its meaning changed). `epochSince` stamps the
  // mint time for retention pruning (EpochRetention.js).
  const adkEnvelopes = [{
    accountId: altAccountId,
    keyEpoch: 0,
    userId: altAccountId,
    sealedAdk: b64(sodium, sodium.crypto_box_seal(epoch.epochSk, idEnc.publicKey)),
    epochSince: ts,
  }]

  // Genesis log triplet (lateGenesis omitted — fresh account at signup),
  // mirroring shared/translog.js commitDocs.
  const log = []
  let seq = 0
  let prevHash = ''
  const push = (core, newSignerSk = null) => {
    const entry = signEntry(sodium,
      { accountId: altAccountId, seq, prevHash, ts, ...core }, idSign.privateKey, newSignerSk)
    log.push(entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
  }
  push({
    kind: KIND_IDENTITY, docVersion: identityVersion,
    docHash: docHashIdentity(sodium, identityBundle),
    signerPk: identityBundle.idSignPk, newSignerPk: identityBundle.idSignPk,
  }, idSign.privateKey)
  push({
    kind: KIND_DEVICE_LIST, docVersion: 1,
    docHash: docHashDeviceList(sodium, deviceList),
    signerPk: identityBundle.idSignPk,
  })
  push({
    kind: KIND_MEMBER_LIST, docVersion: 1,
    docHash: docHashMemberList(sodium, memberList),
    signerPk: identityBundle.idSignPk,
  })

  return { email, identityBundle, deviceList, memberList, adkEnvelopes, log }
}

/**
 * Run the signup bootstrap when appropriate. Returns one of:
 *   { status: 'skipped' }  — not configured / not a new account / no marker
 *   { status: 'exists' }   — account already has an identity
 *   { status: 'created', recoveryCode, recoveryCodeGrouped, passkeyRegistered }
 *   { status: 'failed' }   — logged, marker set, retried at next login
 * Never throws, never blocks signup.
 *
 * `acquirePasskey({ email, required, prfSaltB64 })` is the UI hook (the
 * consent dialog): it returns { credentialIdB64, prfOutput } after a
 * successful ceremony, or null when skipped.
 */
export async function ensureSignupBootstrap({ email, isNewAccount, acquirePasskey }) {
  try {
    if (!E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    if (!isNewAccount && !localStorage.getItem(pendingKey(email))) {
      return { status: 'skipped' }
    }

    const probe = (await E2eeApi.getAccount(email)).data
    if (probe.hasIdentity) {
      localStorage.removeItem(pendingKey(email))
      return { status: 'exists' }
    }
    if (!probe.altAccountId) {
      throw new Error('account has no altAccountId')
    }
    const altAccountId = probe.altAccountId

    const sodium = await getSodium()
    const minted = await mintIdentity(sodium, { email, acquirePasskey, identityVersion: 1 })

    const payload = buildBootstrap(sodium, {
      email, altAccountId, wraps: minted.wraps, idSign: minted.idSign, idEnc: minted.idEnc, epoch: minted.epoch,
    })
    await E2eeApi.bootstrapAccount(payload)
    // A browser that pinned this account's transparency head under a PREVIOUS
    // identity (a dev re-genesis: partition wiped, bootstrapped again) would
    // otherwise refuse the fresh chain as a rollback until the pin is cleared
    // by hand (2026-09-15). The reset path already forgets it the same way.
    forgetPinnedHead(altAccountId)

    await Keystore.saveIdentity(email, { secrets: minted.secrets, altAccountId, email })
    localStorage.removeItem(pendingKey(email))

    console.log(`nc1 bootstrap complete for ${email} (passkey=${minted.passkeyRegistered})`)
    return {
      status: 'created',
      recoveryCode: minted.recoveryCode,
      recoveryCodeGrouped: recoveryCodeDisplay(minted.recoveryCode),
      passkeyRegistered: minted.passkeyRegistered,
    }
  } catch (e) {
    console.log('nc1 bootstrap failed (will retry at next login)')
    console.log(e)
    try {
      localStorage.setItem(pendingKey(email), String(Date.now()))
    } catch { /* storage unavailable — retry naturally on a later login */ }
    return { status: 'failed' }
  }
}

/**
 * Generate a brand-new identity (X25519 + Ed25519), the epoch-0 keypair, and
 * the identity's wrap set: a fresh 160-bit recovery code (always) and, where
 * the platform supports WebAuthn PRF and policy allows, a new passkey wrap.
 * Shared by the signup bootstrap and the viewer-only identity reset.
 */
async function mintIdentity(sodium, { email, acquirePasskey, identityVersion }) {
  const idSign = sodium.crypto_sign_keypair()
  const idEnc = sodium.crypto_box_keypair()
  const epoch = epochKeypair(sodium)
  const secrets = {
    id_enc_pk: b64(sodium, idEnc.publicKey),
    id_enc_sk: b64(sodium, idEnc.privateKey),
    id_sign_pk: b64(sodium, idSign.publicKey),
    id_sign_sk: b64(sodium, idSign.privateKey),
    identity_version: identityVersion,
  }
  const secretsBytes = new TextEncoder().encode(JSON.stringify(secrets))

  // Recovery code — always. This is the guaranteed backstop.
  const recoveryCode = generateRecoveryCode(sodium)
  const wraps = { recovery: wrapSecrets(sodium, recoveryCode, secretsBytes) }

  // Passkey — where the platform supports PRF, governed by
  // VUE_APP_E2EE_PASSKEY_MODE (PasskeyPolicy). The WebAuthn ceremony runs
  // inside the consent dialog's click handler (a real user gesture);
  // `acquirePasskey` resolves with the material, or null when the user
  // skipped (optional mode). Failure still degrades to recovery-code-only —
  // in `required` mode the login catch-up (ensurePasskeyEnrollment) keeps
  // demanding enrollment until it succeeds.
  let passkeyRegistered = false
  try {
    if (passkeyMode() !== 'off' && acquirePasskey && await prfProbablySupported()) {
      const prfSaltB64 = generatePrfSaltB64()
      const material = await acquirePasskey({
        email,
        required: passkeyMode() === 'required',
        prfSaltB64,
      })
      if (material) {
        wraps.webauthn = [wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
          credentialId: material.credentialIdB64,
          prfSalt: prfSaltB64,
        })]
        sodium.memzero(material.prfOutput)
        passkeyRegistered = true
        // Remember that THIS browser minted the credential (UI hint only).
        await Keystore.recordLocalPasskey(email, material.credentialIdB64)
      }
    }
  } catch (e) {
    console.log('passkey registration skipped:', e && e.message)
  }

  return { idSign, idEnc, epoch, secrets, wraps, recoveryCode, passkeyRegistered }
}

/**
 * Finding ⑯: a VIEWER-ONLY account (a report recipient) that lost every unlock
 * method — no passkey, no recovery code, no linked browser — publishes a
 * brand-new identity at identityVersion old+1. The service re-genesises the
 * account's partition (it refuses accounts with devices or other members).
 * Nothing is recoverable from the old identity and nothing needs to be: the
 * account holds no encrypted data of its own. Every owner who shared reports
 * with it keeps the OLD keys in their member list and must re-verify the new
 * safety number and re-grant (Emails.vue "needs re-approval").
 *
 * OWNER scope (OWNER-RESET.md, 2026-09-22): `scope: 'owner'` plus `idToken`
 * from a fresh sign-in (Reauth.js). The account HAS monitored devices and
 * members; the service re-authenticates, refuses a locked account, wipes
 * the partition in phases, re-genesises it, queues the history purge and
 * tells everyone. A reset that died mid-wipe (probe `reset.state ===
 * 'wiping'`, no identity) is finished by the same call — the previous
 * version then comes from the marker instead of the (gone) bundle.
 *
 * Throws on failure (the caller shows the error). Returns
 * { recoveryCode, recoveryCodeGrouped, passkeyRegistered, identityVersion,
 *   resetAt, purge, resumed } — `purge` is the job the service queued
 * ({ jobId, state, keepFromMs }) or null when the caller must start the
 * purge itself (custody-signed; it holds the new key now).
 */
export async function resetIdentity({ email, acquirePasskey, scope = 'viewer', idToken = null }) {
  email = String(email).toLowerCase()
  if (!E2eeApi.isConfigured()) throw new Error('Encryption is not available for this environment.')
  const owner = scope === 'owner'

  const probe = (await E2eeApi.getAccount(email)).data
  if (!probe.altAccountId) throw new Error('This account has no encryption identity to reset.')
  const altAccountId = probe.altAccountId
  let previousVersion
  if (probe.hasIdentity) {
    const current = (await E2eeApi.getIdentityBundle(email)).data
    previousVersion = Number.isSafeInteger(current?.identityVersion) ? current.identityVersion : 1
  } else if (owner && probe.reset && probe.reset.state === 'wiping'
      && Number.isSafeInteger(probe.reset.previousIdentityVersion)) {
    // Resuming: the old identity is already gone, the marker remembers its version.
    previousVersion = probe.reset.previousIdentityVersion
  } else {
    throw new Error('This account has no encryption identity to reset.')
  }
  const identityVersion = previousVersion + 1

  const sodium = await getSodium()
  const minted = await mintIdentity(sodium, { email, acquirePasskey, identityVersion })
  const payload = buildBootstrap(sodium, {
    email, altAccountId, wraps: minted.wraps, idSign: minted.idSign, idEnc: minted.idEnc, epoch: minted.epoch,
    identityVersion,
  })
  let response
  try {
    const res = await E2eeApi.resetAccountIdentity(owner ? { ...payload, scope: 'owner', idToken } : payload)
    response = (res && res.data) || {}
  } catch (e) {
    // An owner reset that failed on the wire may still have landed: the route
    // runs a batched wipe under API Gateway's 29 s cap. Refusals are final;
    // anything else is checked against the account before giving up, because
    // the keys minted above are the only copy of the new identity.
    if (!owner || isFinalRefusal(e)) throw e
    const after = await E2eeApi.getAccount(email).then((r) => r.data).catch(() => null)
    const bundle = after && after.hasIdentity && after.identityVersion === identityVersion
      ? await E2eeApi.getIdentityBundle(email).then((r) => r.data).catch(() => null)
      : null
    if (!bundle || bundle.idSignPk !== payload.identityBundle.idSignPk) throw e
    console.log('nc1 owner reset: the request failed on the wire but the reset landed - continuing', e && e.message)
    response = { identityVersion, resetAt: after.reset && after.reset.at, purge: null, resumed: false }
  }

  // This browser now holds the new identity. Any pinned transparency head
  // for the account belongs to the chain that was just replaced (the
  // Decryptor's store is the one that matters — fixed 2026-09-10, it used
  // to clear the vendored module's unused key).
  forgetPinnedHead(altAccountId)
  await Keystore.saveIdentity(email, { secrets: minted.secrets, altAccountId, email })
  localStorage.removeItem(pendingKey(email))

  console.log(`nc1 identity RESET complete for ${email} (${scope}, v${identityVersion}, passkey=${minted.passkeyRegistered})`)
  return {
    recoveryCode: minted.recoveryCode,
    recoveryCodeGrouped: recoveryCodeDisplay(minted.recoveryCode),
    passkeyRegistered: minted.passkeyRegistered,
    identityVersion,
    resetAt: Number.isSafeInteger(response.resetAt) ? response.resetAt : null,
    purge: response.purge || null,
    resumed: !!response.resumed,
  }
}

/** A refusal the service meant (bad request, re-auth, locked, unknown, conflict) — never retried or re-probed. */
function isFinalRefusal(e) {
  const status = e && e.response && e.response.status
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 409
}

/**
 * Add a webauthn wrap to the account's identity bundle, signed by the cached
 * identity — the shared core of the required-mode login catch-up AND the
 * post-link enrollment (a freshly linked browser registering its own
 * passkey). `requireAbsent: true` keeps catch-up semantics (skip when ANY
 * webauthn wrap exists); false appends alongside existing wraps. The PRF
 * salt is FIXED PER ACCOUNT (unlock evaluates the PRF before knowing which
 * credential answers), so an existing wrap's salt is reused. Throws on hard
 * failure; returns { status: 'exists' | 'deferred' | 'enrolled' }.
 */
export async function enrollPasskeyWrap({ email, cached, acquirePasskey, required, requireAbsent }) {
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  const existing = (bundle.wraps && bundle.wraps.webauthn) || []
  if (requireAbsent && existing.length) {
    return { status: 'exists' }
  }

  const prfSaltB64 = existing.length ? existing[0].prfSalt : generatePrfSaltB64()
  const material = await acquirePasskey({ email, required: !!required, prfSaltB64 })
  if (!material) {
    return { status: 'deferred' }
  }

  const sodium = await getSodium()
  const secretsBytes = new TextEncoder().encode(JSON.stringify(cached.secrets))
  const value = {
    ...bundle,
    wraps: {
      ...bundle.wraps,
      webauthn: [...existing, wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
        credentialId: material.credentialIdB64,
        prfSalt: prfSaltB64,
      })],
    },
  }
  sodium.memzero(material.prfOutput)

  const sig = b64(sodium, sodium.crypto_sign_detached(
    canonicalizeBytes(value), ub64(sodium, cached.secrets.id_sign_sk)))
  await E2eeApi.putIdentityBundle(email, value, sig)
  // Remember that THIS browser minted the credential (UI hint only).
  await Keystore.recordLocalPasskey(email, material.credentialIdB64)
  return { status: 'enrolled' }
}

/**
 * Required-mode login catch-up: once VUE_APP_E2EE_PASSKEY_MODE flips to
 * `required`, accounts that deferred enrollment (identity bundle has no
 * webauthn wrap) must register a passkey at login. Only possible where this
 * browser holds the cached identity (we can't wrap secrets we don't have —
 * keyless browsers pick this up after unlock). Publishes the added wrap
 * through the signature-checked identityBundle write.
 * Returns { status: 'skipped' | 'exists' | 'unsupported' | 'enrolled' |
 * 'deferred' | 'failed' }. Never throws, never blocks login.
 */
export async function ensurePasskeyEnrollment({ email, acquirePasskey }) {
  try {
    if (passkeyMode() !== 'required' || !acquirePasskey || !E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    const cached = await Keystore.loadIdentity(email)
    if (!cached || !cached.secrets) {
      return { status: 'skipped' }
    }
    if (!(await prfProbablySupported())) {
      return { status: 'unsupported' }
    }

    const result = await enrollPasskeyWrap({
      email, cached, acquirePasskey, required: true, requireAbsent: true,
    })
    if (result.status === 'enrolled') {
      console.log(`nc1 passkey catch-up complete for ${email}`)
    }
    return result
  } catch (e) {
    console.log('nc1 passkey catch-up failed (will retry at next login)')
    console.log(e)
    return { status: 'failed' }
  }
}
